import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PawPrint, Save, UserPlus, X as XIcon } from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEX_OPTIONS = [
  'Intact Male',
  'Neutered Male',
  'Intact Female',
  'Spayed Female',
  'Unknown',
];

// Labeled is declared at module scope so it keeps its identity across
// parent re-renders — prevents the inputs inside from losing focus
// after every keystroke ("one letter at a time" bug).
const Labeled = ({ label, children, span }) => (
  <label className={`block ${span || ''}`}>
    <span className="block text-xs font-medium text-foreground/60 uppercase tracking-wide mb-1">
      {label}
    </span>
    {children}
  </label>
);

const deriveAgeFromDob = (dob) => {
  if (!dob) return '';
  try {
    const d = new Date(dob + 'T00:00:00');
    const now = new Date();
    const months =
      (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth()) -
      (now.getDate() < d.getDate() ? 1 : 0);
    if (months < 0) return '';
    if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
    const years = Math.floor(months / 12);
    const rem = months % 12;
    return rem === 0
      ? `${years} year${years === 1 ? '' : 's'}`
      : `${years}y ${rem}m`;
  } catch {
    return '';
  }
};

export const CreatePet = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const authHeaders = useMemo(
    () => ({ Authorization: `Basic ${btoa(':' + password)}` }),
    [password]
  );

  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_id: '',
    name: '',
    species: 'Dog',
    breed: '',
    date_of_birth: '',
    age: '',
    sex: '',
    color_markings: '',
    microchip_number: '',
    notes: '',
  });

  // New-client inline form (collapsible)
  const [showNewClient, setShowNewClient] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });

  const loadClients = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients`, { headers: authHeaders });
      if (res.ok) setClients(await res.json());
    } catch (err) {
      logger.error('clients fetch:', err);
    }
  };

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateClient = (k, v) => setNewClient((c) => ({ ...c, [k]: v }));

  const handleDobChange = (value) => {
    setForm((f) => ({
      ...f,
      date_of_birth: value,
      // Auto-fill age from DOB only if the user hasn't entered a manual age
      age: f.age && f.age.trim() && !/^\d+(y|\syears?|\smonths?|m\s|\sm|\sy|months?|years?)$/i.test(f.age)
        ? f.age
        : deriveAgeFromDob(value),
    }));
  };

  const createClientInline = async () => {
    if (!newClient.name.trim() || !newClient.email.trim()) {
      alert('Client name and email are required.');
      return;
    }
    setCreatingClient(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/clients`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(newClient),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const created = await res.json();
      // Refresh list and select the new client
      await loadClients();
      setForm((f) => ({ ...f, client_id: created.id }));
      setShowNewClient(false);
      setNewClient({ name: '', email: '', phone: '', address: '' });
    } catch (err) {
      logger.error('create client inline:', err);
      alert(`Could not create client: ${err.message}`);
    } finally {
      setCreatingClient(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.name) {
      alert('Client and pet name are required.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/pets`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const pet = await res.json();
        navigate(`/admin/pets/${pet.id}`);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`Create failed: ${err.detail || 'Unknown error'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-primary flex items-center gap-2">
            <PawPrint className="w-5 h-5" /> New Pet
          </h1>
          <button
            onClick={() => navigate('/admin/pets')}
            className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Cancel
          </button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6 max-w-2xl">
        <form
          onSubmit={submit}
          className="bg-white border border-border rounded-xl p-6 space-y-4"
          lang="en-GB"
          spellCheck="true"
        >
          {/* Client picker + inline new-client option */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="block text-xs font-medium text-foreground/60 uppercase tracking-wide">
                Client *
              </span>
              <button
                type="button"
                onClick={() => setShowNewClient((s) => !s)}
                data-testid="toggle-new-client"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                {showNewClient ? (
                  <><XIcon className="w-3 h-3" /> Cancel new client</>
                ) : (
                  <><UserPlus className="w-3 h-3" /> Add new client</>
                )}
              </button>
            </div>
            {!showNewClient && (
              <select
                required
                value={form.client_id}
                onChange={(e) => update('client_id', e.target.value)}
                data-testid="create-pet-client"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                <option value="">— Select a client —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.email})
                  </option>
                ))}
              </select>
            )}
            {showNewClient && (
              <div
                className="border border-primary/30 bg-primary/5 rounded-lg p-3 space-y-2"
                data-testid="new-client-panel"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Labeled label="Name *">
                    <input
                      autoFocus
                      value={newClient.name}
                      onChange={(e) => updateClient('name', e.target.value)}
                      data-testid="new-client-name"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                    />
                  </Labeled>
                  <Labeled label="Email *">
                    <input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => updateClient('email', e.target.value)}
                      data-testid="new-client-email"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                    />
                  </Labeled>
                  <Labeled label="Phone">
                    <input
                      value={newClient.phone}
                      onChange={(e) => updateClient('phone', e.target.value)}
                      data-testid="new-client-phone"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                    />
                  </Labeled>
                  <Labeled label="Address">
                    <input
                      value={newClient.address}
                      onChange={(e) => updateClient('address', e.target.value)}
                      data-testid="new-client-address"
                      className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                    />
                  </Labeled>
                </div>
                <button
                  type="button"
                  onClick={createClientInline}
                  disabled={creatingClient}
                  data-testid="save-new-client"
                  className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {creatingClient ? 'Saving…' : 'Save client & use'}
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Labeled label="Pet name *">
              <input
                required
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                data-testid="create-pet-name"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </Labeled>
            <Labeled label="Species">
              <select
                value={form.species}
                onChange={(e) => update('species', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                {['Dog', 'Cat', 'Bird', 'Rabbit', 'Other'].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </Labeled>
            <Labeled label="Breed">
              <input
                value={form.breed}
                onChange={(e) => update('breed', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </Labeled>
            <Labeled label="Date of birth">
              <input
                type="date"
                value={form.date_of_birth}
                onChange={(e) => handleDobChange(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </Labeled>
            <Labeled label="Age">
              <input
                value={form.age}
                onChange={(e) => update('age', e.target.value)}
                placeholder="Auto-filled from DOB — or type e.g. 3 years, 8 months"
                data-testid="create-pet-age"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              />
            </Labeled>
            <Labeled label="Sex / reproductive status">
              <select
                value={form.sex}
                onChange={(e) => update('sex', e.target.value)}
                data-testid="create-pet-sex"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
              >
                <option value="">—</option>
                {SEX_OPTIONS.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </Labeled>
            <Labeled label="Microchip number" span="md:col-span-2">
              <input
                value={form.microchip_number}
                onChange={(e) => update('microchip_number', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
              />
            </Labeled>
          </div>

          <Labeled label="Markings / colour">
            <input
              value={form.color_markings}
              onChange={(e) => update('color_markings', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </Labeled>
          <Labeled label="Notes">
            <textarea
              rows="3"
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </Labeled>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => navigate('/admin/pets')}
              className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="create-pet-submit"
              className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {saving ? 'Creating…' : 'Create Pet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
